<script setup lang="ts">
import { TABLE_FOOTER_TYPE } from "./useTable";
import { useTableFooter, type TableFooterProps } from "./useTableFooter";

/**
 * **TableFooter** (`e-table-footer`)
 *
 * Renders the footer section of a `<e-table>`.
 * Supports a `sticky` prop to keep the footer visible when the table body scrolls.
 * This component is a structural wrapper — it holds no logic.
 *
 * @example
 * ```html
 * <e-table scroll="vertical" max-height="300px">
 *   <e-table-body>
 *     <!-- rows... -->
 *   </e-table-body>
 *   <e-table-footer>
 *     <e-table-row>
 *       <e-table-cell>Total</e-table-cell>
 *       <e-table-cell>$1,234.00</e-table-cell>
 *     </e-table-row>
 *   </e-table-footer>
 * </e-table>
 *
 * <!-- Sticky footer -->
 * <e-table-footer sticky>
 *   <e-table-row>
 *     <e-table-cell>Total</e-table-cell>
 *   </e-table-row>
 * </e-table-footer>
 * ```
 */

defineOptions({
  name: TABLE_FOOTER_TYPE,
});

const props = withDefaults(defineProps<TableFooterProps>(), {
  sticky: false,
});

const { classes } = useTableFooter({ props });
</script>

<style lang="css">
/* Ensure the host does not interfere with the CSS grid layout of the parent table. */
:host {
  display: contents;
}
</style>

<template>
  <div part="footer" :class="classes">
    <slot />
  </div>
</template>
